import { Check } from 'lucide-react';
import { Checkbox as C, Switch as Sw, Tabs as T } from 'radix-ui';
import type { ReactNode } from 'react';
import { cn } from '../cn';

export function Switch({
  checked,
  onCheckedChange,
  id,
  disabled,
  label,
}: {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  id?: string;
  disabled?: boolean;
  label?: string;
}) {
  return (
    <Sw.Root
      id={id}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      aria-label={label}
      className="relative inline-flex h-6 w-10 shrink-0 items-center rounded-full bg-line-strong transition-colors duration-150 data-[state=checked]:bg-primary disabled:opacity-50"
    >
      <Sw.Thumb className="block size-5 translate-x-0.5 rounded-full bg-white shadow-hairline transition-transform duration-150 ease-out data-[state=checked]:translate-x-[18px]" />
    </Sw.Root>
  );
}

export function Checkbox({
  checked,
  onCheckedChange,
  id,
  label,
  disabled,
}: {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  id?: string;
  label?: ReactNode;
  disabled?: boolean;
}) {
  const box = (
    <C.Root
      id={id}
      checked={checked}
      disabled={disabled}
      onCheckedChange={(v) => onCheckedChange(v === true)}
      className="flex size-5 shrink-0 items-center justify-center rounded-xs border border-line-strong bg-surface transition-colors data-[state=checked]:border-primary data-[state=checked]:bg-primary disabled:opacity-50"
    >
      <C.Indicator>
        <Check className="size-3.5 text-white" strokeWidth={3} />
      </C.Indicator>
    </C.Root>
  );
  if (!label) return box;
  return (
    <label className="inline-flex cursor-pointer items-center gap-2.5 text-base text-ink">
      {box}
      {label}
    </label>
  );
}

export interface TabItem {
  value: string;
  label: ReactNode;
  count?: number;
  content: ReactNode;
}

/** Underlined tabs for switching views inside one page. */
export function Tabs({
  items,
  value,
  onValueChange,
  className,
}: {
  items: TabItem[];
  value?: string;
  onValueChange?: (v: string) => void;
  className?: string;
}) {
  return (
    <T.Root
      value={value}
      defaultValue={value === undefined ? items[0]?.value : undefined}
      onValueChange={onValueChange}
      className={className}
    >
      <T.List className="flex gap-6 border-b border-line">
        {items.map((t) => (
          <T.Trigger
            key={t.value}
            value={t.value}
            className="-mb-px flex h-11 items-center gap-2 border-b-2 border-transparent text-base font-medium text-ink-3 transition-colors hover:text-ink data-[state=active]:border-primary data-[state=active]:text-ink"
          >
            {t.label}
            {t.count !== undefined && (
              <span className="rounded-sm bg-rail px-1.5 text-xs text-ink-3 tnum">{t.count}</span>
            )}
          </T.Trigger>
        ))}
      </T.List>
      {items.map((t) => (
        <T.Content key={t.value} value={t.value} className="pt-5 focus:outline-none">
          {t.content}
        </T.Content>
      ))}
    </T.Root>
  );
}

export interface SegmentOption<V extends string> {
  value: V;
  label: ReactNode;
}

/** Compact segmented control for 2–5 mutually exclusive options (period, mode). */
export function Segmented<V extends string>({
  value,
  onChange,
  options,
  size = 'md',
  className,
  label,
}: {
  value: V;
  onChange: (v: V) => void;
  options: SegmentOption<V>[];
  size?: 'sm' | 'md';
  className?: string;
  label: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn('inline-flex rounded-md bg-rail p-0.5', className)}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            'rounded-sm px-3 font-medium text-ink-3 transition-[background-color,color,box-shadow] duration-150 hover:text-ink',
            size === 'sm' ? 'h-7 text-sm' : 'h-9 text-base',
            value === o.value && 'key text-ink',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
